import React, { useEffect } from "react";
import { observer } from "mobx-react";
import styled from "styled-components";
import { Container, Typography, Paper } from "@mui/material";
import {
  BiomonitoringWidget,
  DietaryGoalsWidget,
  WeeklyMealsWidget,
} from "../components/Dashboard";
import { CartStatusWidget } from "../components/Dashboard";
import { dashboardController } from "../modules/controllers";
import { userStore } from "../modules/stores";

const DashboardWrapper = styled.div`
  background-color: #f8f9fa;
  min-height: 100vh;
  padding: 20px 0;
`;

const WelcomeSection = styled(Paper)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 32px;
  margin-bottom: 24px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const WelcomeTitle = styled(Typography)`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 8px;
`;

const WelcomeSubtitle = styled(Typography)`
  font-size: 1.1rem;
  opacity: 0.9;
`;

const StatCard = styled(Paper)`
  padding: 24px;
  text-align: center;
  color: white;
  border-radius: 12px;
  min-height: 100%;
  box-sizing: border-box;
`;

const DashboardGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 24px;

  & > * {
    flex: 1 1 100%;
    min-width: 0;
    
    @media (min-width: 900px) {
      flex: 1 1 calc(50% - 12px);
    }
    
    @media (min-width: 1200px) {
      flex: 1 1 calc(33.333% - 16px);
    }
  }
`;

const StatsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 32px;
  
  & > * {
    flex: 1 1 100%;
    min-width: 0;
    
    @media (min-width: 600px) {
      flex: 1 1 calc(50% - 8px);
    }
    
    @media (min-width: 900px) {
      flex: 1 1 calc(25% - 12px);
    }
  }
`;

export const Home = observer(() => {
  useEffect(() => {
    // Initialize dashboard when component mounts
    if (userStore.isLoggedIn && userStore.userObj) {
      dashboardController.initializeDashboard();
    }
  }, []);

  const userName =
    userStore.userObj?.firstName || userStore.userObj?.username || "User";
  const currentHour = new Date().getHours();
  const currentDateString = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const getGreeting = () => {
    if (currentHour < 12) return "Good morning";
    if (currentHour < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <DashboardWrapper>
      <Container maxWidth="xl">
        {/* Welcome Section */}
        <WelcomeSection elevation={0}>
          <WelcomeTitle variant="h1">
            {getGreeting()}, {userName}! 👋
          </WelcomeTitle>
          <WelcomeSubtitle variant="subtitle1">
            {currentDateString} • Here's your health and nutrition overview for today
          </WelcomeSubtitle>
        </WelcomeSection>

        {/* Dashboard Widgets */}
        <DashboardGrid>
          <BiomonitoringWidget controller={dashboardController} />
          <WeeklyMealsWidget controller={dashboardController} />
          <CartStatusWidget />
          <DietaryGoalsWidget controller={dashboardController} />
        </DashboardGrid>

        {/* Quick Stats Section */}
        <StatsGrid>
          <StatCard
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
              {Math.round(
                (dashboardController.getDietaryGoals.caloriesConsumed /
                  dashboardController.getDietaryGoals.caloriesTarget) *
                  100 || 0
              )}
              %
            </Typography>
            <Typography variant="body2">Daily Calories</Typography>
          </StatCard>

          <StatCard
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
              {dashboardController.getWeeklyMeals.length}
            </Typography>
            <Typography variant="body2">Meals Planned</Typography>
          </StatCard>

          <StatCard
            style={{
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
              {Math.round(
                (dashboardController.getDietaryGoals.waterConsumed /
                  dashboardController.getDietaryGoals.waterTarget) *
                  100 || 0
              )}
              %
            </Typography>
            <Typography variant="body2">Water Goal</Typography>
          </StatCard>

          <StatCard
            style={{
              background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
              {
                Object.values(dashboardController.getBiomonitoringData)
                  .slice(0, -1)
                  .filter(
                    (metric) =>
                      metric.status === "normal" ||
                      metric.status === "good" ||
                      metric.status === "sufficient"
                  ).length
              }
            </Typography>
            <Typography variant="body2">Healthy Metrics</Typography>
          </StatCard>
        </StatsGrid>
      </Container>
    </DashboardWrapper>
  );
});
